import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, provideRouter } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import { IonicRouteStrategy } from '@ionic/angular';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { IconsModule, SVGIconModule } from '@progress/kendo-angular-icons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { MessageService } from '@progress/kendo-angular-l10n';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { ListViewModule } from '@progress/kendo-angular-listview';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { PopupModule } from '@progress/kendo-angular-popup';
import { TreeViewModule } from '@progress/kendo-angular-treeview';
import { UploadModule } from '@progress/kendo-angular-upload';
import { RECAPTCHA_SETTINGS, RecaptchaSettings } from 'ng-recaptcha-2';
import { captcha } from 'src/environments/environment';

import { MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './app/app-msal-extensions';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { AppbarService } from './app/components/appbar/appbar-service';
import { AdminAuthGuard, AttendantAuthGuard } from './app/services/auth-guards.service';
import { ConfigService } from './app/services/config.service';
import { InterceptorService } from './app/services/http-interceptor.service';
import { CustomMessagesService } from './app/services/kendo-message-override.service';
import MsalServiceWrapper from './app/services/msal.wrapper.service';
import { PROVIDERDIRECTORY_API_BASE_URL } from './app/services/providerdirectoryapi/providerdirectoryfunctionapi.services';
import { environment } from './environments/environment';

const appInitializerFn = (configService: ConfigService) => {
  return () => {
    return configService.setConfig();
  };
};

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      NotificationModule,
      BrowserModule,
      IconsModule,
      FormsModule,
      ReactiveFormsModule,
      InputsModule,
      UploadModule,
      TreeViewModule,
      LayoutModule,
      ListViewModule,
      PopupModule,
      DialogsModule,
      IndicatorsModule,
      MsalModule,
      SVGIconModule
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptorService,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: { siteKey: captcha.siteKey } as RecaptchaSettings,
    },
    {
      provide: MessageService,
      useClass: CustomMessagesService,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },
    DatePipe,
    AppbarService,
    ConfigService,
    provideAppInitializer(() => {
      const initializerFn = appInitializerFn(inject(ConfigService));
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(routes),
    AttendantAuthGuard,
    MsalServiceWrapper,
    AdminAuthGuard,
    {
      provide: PROVIDERDIRECTORY_API_BASE_URL,
      useFactory: () => environment.providerDirectoryApiHost,
    },
    provideAnimations(),
  ],
}).catch(err => console.log(err));
